import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

// Translation resources
const resources = {
  en: {
    translation: {
      // Navigation
      nav: {
        resume: 'resume',
        projects: 'projects',
        posts: 'posts'
      },

      // Header
      header: {
        darkMode: 'Switch to dark mode',
        lightMode: 'Switch to light mode',
        toggleMenu: 'Toggle mobile menu'
      },

      // Home page
      home: {
        title: 'About',
        intro: '- Artificial Intelligence Engineering student at Hacettepe University, specializing in',
        neuralNetworks: 'Neural Networks',
        computerVision: 'Computer Vision',
        and: 'and',
        focusAreas: 'Technical focus areas:',
        areas: {
          neuralNetworks: 'Neural Networks & Deep Learning',
          neuralNetworksDesc: 'Multimodal learning, Transformers, CNN architectures, PyTorch',
          computerVision: 'Computer Vision',
          computerVisionDesc: 'Object detection, aerial imagery, image processing',
          bioinformatics: 'Bioinformatics & Drug Discovery',
          bioinformaticsDesc: 'Multimodal chemical language models, drug-target prediction (MuChem, DEEPScreen2)',
          agenticAI: 'Agentic AI & LLMs',
          agenticAIDesc: 'RAG systems, LLM workflows, autonomous agents, workflow automation'
        },
        conclusion: 'Currently working as Junior Data Scientist at Invent Analytics, building autonomous AgenticAI workflows and RAG pipelines. Also contributing as AI Research Intern at Hacettepe University\'s Biological Data Science Laboratory, co-architecting a novel Multimodal Chemical Language Model. Key projects include MuChem (multimodal drug discovery), DEEPScreen2 (AI-based drug interaction predictor), and Early Risk Analysis in MOOCs using sequential data modeling.'
      },

      // Projects page
      projects: {
        title: 'Projects',
        role: 'Researcher & Developer',
        github: 'GitHub',
        muchem: {
          title: 'MuChem | Multimodal Deep Learning for Drug Discovery',
          description: 'Architected a novel deep learning framework that fuses 1D SMILES sequences and 2D molecular graphs to predict drug properties with high precision. Designed a custom dual-encoder mechanism using PyTorch, enabling the model to learn rich representations from heterogeneous chemical data.',
          technologies: ['PyTorch', 'Deep Learning', 'Multimodal Learning', 'Drug Discovery', 'Transformers', 'SMILES']
        },
        deepscreen2: {
          title: 'DEEPScreen2 | AI-based Drug Interaction Predictor',
          description: 'Enhanced the legacy CNN-based pipeline by implementing rotation-invariant image augmentation strategies, increasing model robustness on large-scale datasets. Integrated SHAP-based Explainable AI (XAI) modules to visualize molecular interactions, making the "black-box" model interpretable for biologists.',
          technologies: ['Python', 'CNN', 'Machine Learning', 'XAI', 'SHAP', 'Bioinformatics', 'Neural Networks']
        },
        mooc: {
          title: 'Early Risk Analysis in MOOCs | Sequential Data Modeling',
          description: 'Engineered an Early Warning System (EWS) using LSTM networks to predict student dropout risks based on sequential behavioral data from initial course weeks. Leveraged XGBoost, LightGBM and classical machine learning algorithms to achieve robust prediction performance and applied XAI techniques to identify root causes of failures.',
          technologies: ['LSTM', 'XGBoost', 'LightGBM', 'Sequential Modeling', 'XAI', 'Machine Learning']
        },
        tec: {
          title: 'Ionospheric TEC Visualizer | GNSS Data Analysis Tool',
          description: 'Built an interactive visualization tool using Python to analyze Total Electron Content (TEC) data derived from GNSS measurements. Devised parsing algorithms for IONEX formats and designed dashboards to monitor ionospheric variations and space weather effects.',
          technologies: ['Python', 'GNSS', 'Data Visualization', 'IONEX', 'Space Weather']
        },
        webInfra: {
          title: 'End-to-End Web Infrastructure Deployment',
          description: 'Orchestrated the end-to-end deployment of two production-ready web applications using React (Frontend) and Spring Boot (Backend). Provisioned a secure Linux VPS environment, configuring Docker containers and Nginx reverse proxy for efficient traffic management. Automated database management with MySQL and established CI pipelines for seamless updates.',
          technologies: ['React', 'Spring Boot', 'Docker', 'Nginx', 'MySQL', 'CI/CD', 'Linux', 'VPS']
        }
      },

      // Resume page
      resume: {
        title: 'Resume',
        download: '📄 Download as PDF',
        contact: {
          title: 'Contact Information',
          email: 'Email',
          location: 'Location',
          ankara: 'Ankara, Turkey',
          linkedin: 'LinkedIn',
          github: 'GitHub'
        },
        education: {
          title: 'Education',
          degree: 'Bachelor of Science (BSc) in Artificial Intelligence Engineering',
          university: 'Hacettepe University',
          duration: 'Sep 2022 – Present',
          location: 'Ankara, Turkey',
          relevantCoursework: 'Relevant Coursework',
          coursework: 'Machine Learning, Deep Learning, Data Intensive Applications, Bioinformatics, AI Principles, Data Structures & Algorithms, OOP - Object Oriented Programming',
          languages: 'Languages',
          turkish: 'Turkish (Native)',
          english: 'English (B2)'
        },
        experience: {
          title: 'Work Experience',
          juniorDataScientist: 'Junior Data Scientist',
          inventAnalytics: 'Invent Analytics',
          duration: 'May 2025 – Present',
          location: 'Ankara, Turkey',
          responsibilities: [
            'Engineered autonomous AgenticAI workflows using Python, enabling the execution of multi-step decision-making processes that significantly reduced manual oversight in operational tasks',
            'Built scalable Retrieval-Augmented Generation (RAG) pipelines leveraging LLMs and vector databases to improve the accuracy and speed of internal knowledge retrieval',
            'Developed and deployed intelligent agents integrated with GitHub, Slack and Jira APIs, fully automating project tracking routines and team reporting mechanisms'
          ],
          aiResearchIntern: 'AI Research Intern',
          researchDuration: 'Jan 2024 – Present',
          biologicalDataLab: 'Biological Data Science Laboratory, Hacettepe University',
          researchResponsibilities: [
            'Automated end-to-end data processing pipelines by writing custom scripts, which streamlined image generation and API data retrieval for large-scale biological datasets',
            'Co-architected the codebase for a novel Multimodal Chemical Language Model from scratch; designed a custom fusion mechanism using Transformers to process SMILES, molecular texts and molecular graphs simultaneously',
            'Conducted extensive benchmarking experiments using PyTorch to validate the performance of CNN-based models against state-of-the-art architectures'
          ],
          baykarIntern: 'AI Software Technologies Intern',
          baykar: 'Baykar',
          baykarDuration: 'Aug 2025 – Sep 2025',
          baykarLocation: 'Istanbul, Turkey',
          baykarResponsibilities: [
            'Contributed to the development of Computer Vision algorithms for object detection and tracking on aerial imagery',
            'Performed data annotation, model training, and performance evaluation to enhance real-time vision capabilities'
          ]
        },
        projects: {
          title: 'Projects',
          projectParticipant: 'Researcher & Developer',
          muchem: {
            title: 'MuChem | Multimodal Deep Learning for Drug Discovery',
            description: [
              'Architected a novel deep learning framework that fuses 1D SMILES sequences and 2D molecular graphs to predict drug properties with high precision',
              'Designed a custom dual-encoder mechanism using PyTorch, enabling the model to learn rich representations from heterogeneous chemical data'
            ]
          },
          deepscreen2: {
            title: 'DEEPScreen2 | AI-based Drug Interaction Predictor',
            description: [
              'Enhanced the legacy CNN-based pipeline by implementing rotation-invariant image augmentation strategies, increasing model robustness on large-scale datasets',
              'Integrated SHAP-based Explainable AI (XAI) modules to visualize molecular interactions, making the "black-box" model interpretable for biologists'
            ]
          },
          mooc: {
            title: 'Early Risk Analysis in MOOCs | Sequential Data Modeling',
            description: [
              'Engineered an Early Warning System (EWS) using LSTM networks to predict student dropout risks based on sequential behavioral data from initial course weeks',
              'Leveraged XGBoost, LightGBM and classical machine learning algorithms to achieve robust prediction performance and applied XAI techniques to identify root causes of failures'
            ]
          },
          tec: {
            title: 'Ionospheric TEC Visualizer | GNSS Data Analysis Tool',
            description: [
              'Built an interactive visualization tool using Python to analyze Total Electron Content (TEC) data derived from GNSS measurements',
              'Devised parsing algorithms for IONEX formats and designed dashboards to monitor ionospheric variations and space weather effects'
            ]
          },
          webInfra: {
            title: 'End-to-End Web Infrastructure Deployment',
            description: [
              'Orchestrated the end-to-end deployment of two production-ready web applications using React (Frontend) and Spring Boot (Backend)',
              'Provisioned a secure Linux VPS environment, configuring Docker containers and Nginx reverse proxy for efficient traffic management',
              'Automated database management with MySQL and established CI pipelines for seamless updates'
            ]
          }
        },
        skills: {
          title: 'Technical Skills',
          programming: 'Programming & Core',
          programmingList: 'Python, Java, C++, SQL, OOP, Data Structures, Algorithms, Git',
          aiMl: 'AI & Data Science',
          aiMlList: 'PyTorch, Scikit-learn, Pandas, NumPy, LangChain, AgenticAI, LLMs, RAG, Computer Vision (CNN), XAI (SHAP), Transformers',
          webDev: 'Web Development',
          webDevList: 'React.js, HTML/CSS, JavaScript, Streamlit',
          devops: 'DevOps & Cloud',
          devopsList: 'Docker, Docker Compose, Nginx, Linux/VPS Administration, MySQL, CI/CD Pipelines'
        }
      }
    }
  },
  tr: {
    translation: {
      // Navigasyon
      nav: {
        resume: 'özgeçmiş',
        projects: 'projeler',
        posts: 'yazılar'
      },

      // Header
      header: {
        darkMode: 'Karanlık moda geç',
        lightMode: 'Aydınlık moda geç',
        toggleMenu: 'Mobil menüyü aç/kapat'
      },

      // Ana sayfa
      home: {
        title: 'Hakkında',
        intro: '- Hacettepe Üniversitesi Yapay Zeka Mühendisliği öğrencisi,',
        neuralNetworks: 'Sinir Ağları',
        computerVision: 'Bilgisayarlı Görü',
        and: 've',
        focusAreas: 'Teknik odak alanları:',
        areas: {
          neuralNetworks: 'Sinir Ağları ve Derin Öğrenme',
          neuralNetworksDesc: 'Çok modlu öğrenme, Transformers, CNN mimarileri, PyTorch',
          computerVision: 'Bilgisayarlı Görü',
          computerVisionDesc: 'Nesne tespiti, havadan görüntüler, görüntü işleme',
          bioinformatics: 'Biyoinformatik ve İlaç Keşfi',
          bioinformaticsDesc: 'Çok modlu kimyasal dil modelleri, ilaç-hedef tahmini (MuChem, DEEPScreen2)',
          agenticAI: 'Aracı Yapay Zeka ve Büyük Dil Modelleri',
          agenticAIDesc: 'RAG sistemleri, LLM iş akışları, otonom aracılar, iş akışı otomasyonu'
        },
        conclusion: 'Şu anda Invent Analytics\'te Genç Veri Bilimci olarak çalışmakta, otonom Aracı Yapay Zeka iş akışları ve RAG hatları oluşturmaktadır. Ayrıca Hacettepe Üniversitesi Biyolojik Veri Bilimi Laboratuvarı\'nda Yapay Zeka Araştırma Stajyeri olarak katkıda bulunmakta, yeni bir Çok Modlu Kimyasal Dil Modeli\'ni birlikte tasarlamaktadır. Önemli projeler arasında MuChem (çok modlu ilaç keşfi), DEEPScreen2 (yapay zeka tabanlı ilaç etkileşim tahmincisi) ve sıralı veri modelleme kullanarak MOOC\'lerde Erken Risk Analizi bulunmaktadır.'
      },

      // Projeler sayfası
      projects: {
        title: 'Projeler',
        role: 'Araştırmacı ve Geliştirici',
        github: 'GitHub',
        muchem: {
          title: 'MuChem | İlaç Keşfi için Çok Modlu Derin Öğrenme',
          description: 'İlaç özelliklerini yüksek hassasiyetle tahmin etmek için 1D SMILES dizilerini ve 2D moleküler grafikleri birleştiren yeni bir derin öğrenme çerçevesi tasarladım. PyTorch kullanarak özel bir çift kodlayıcı mekanizması tasarladım, modelin heterojen kimyasal verilerden zengin temsiller öğrenmesini sağladım.',
          technologies: ['PyTorch', 'Derin Öğrenme', 'Çok Modlu Öğrenme', 'İlaç Keşfi', 'Transformers', 'SMILES']
        },
        deepscreen2: {
          title: 'DEEPScreen2 | Yapay Zeka Tabanlı İlaç Etkileşim Tahmincisi',
          description: 'Döndürme-değişmez görüntü artırma stratejileri uygulayarak eski CNN tabanlı hattı geliştirdim, büyük ölçekli veri setlerinde model sağlamlığını artırdım. Moleküler etkileşimleri görselleştirmek için SHAP tabanlı Açıklanabilir Yapay Zeka (XAI) modüllerini entegre ettim, "kara kutu" modeli biyologlar için yorumlanabilir hale getirdim.',
          technologies: ['Python', 'CNN', 'Makine Öğrenmesi', 'XAI', 'SHAP', 'Biyoinformatik', 'Sinir Ağları']
        },
        mooc: {
          title: 'MOOC\'lerde Erken Risk Analizi | Sıralı Veri Modelleme',
          description: 'İlk kurs haftalarından sıralı davranışsal verilere dayalı olarak öğrenci ayrılma risklerini tahmin etmek için LSTM ağları kullanarak bir Erken Uyarı Sistemi (EWS) tasarladım. Sağlam tahmin performansı elde etmek için XGBoost, LightGBM ve klasik makine öğrenmesi algoritmalarını kullandım ve başarısızlıkların kök nedenlerini belirlemek için XAI teknikleri uyguladım.',
          technologies: ['LSTM', 'XGBoost', 'LightGBM', 'Sıralı Modelleme', 'XAI', 'Makine Öğrenmesi']
        },
        tec: {
          title: 'İyonosferik TEC Görselleştirici | GNSS Veri Analiz Aracı',
          description: 'GNSS ölçümlerinden türetilen Toplam Elektron İçeriği (TEC) verilerini analiz etmek için Python kullanarak etkileşimli bir görselleştirme aracı oluşturdum. IONEX formatları için ayrıştırma algoritmaları tasarladım ve iyonosferik varyasyonları ve uzay hava etkilerini izlemek için paneller tasarladım.',
          technologies: ['Python', 'GNSS', 'Veri Görselleştirme', 'IONEX', 'Uzay Hava Durumu']
        },
        webInfra: {
          title: 'Uçtan Uca Web Altyapı Dağıtımı',
          description: 'React (Ön Uç) ve Spring Boot (Arka Uç) kullanarak iki üretime hazır web uygulamasının uçtan uca dağıtımını düzenledim. Verimli trafik yönetimi için güvenli bir Linux VPS ortamı sağladım, Docker konteynerleri ve Nginx ters proxy\'yi yapılandırdım. MySQL ile veritabanı yönetimini otomatikleştirdim ve sorunsuz güncellemeler için CI hatları oluşturdum.',
          technologies: ['React', 'Spring Boot', 'Docker', 'Nginx', 'MySQL', 'CI/CD', 'Linux', 'VPS']
        }
      },

      // Özgeçmiş sayfası
      resume: {
        title: 'Özgeçmiş',
        download: '📄 PDF Olarak İndir',
        contact: {
          title: 'İletişim Bilgileri',
          email: 'E-posta',
          location: 'Konum',
          ankara: 'Ankara, Türkiye',
          linkedin: 'LinkedIn',
          github: 'GitHub'
        },
        education: {
          title: 'Eğitim',
          degree: 'Yapay Zeka Mühendisliği Lisans (BSc)',
          university: 'Hacettepe Üniversitesi',
          duration: 'Eylül 2022 – Devam Ediyor',
          location: 'Ankara, Türkiye',
          relevantCoursework: 'İlgili Dersler',
          coursework: 'Makine Öğrenmesi, Derin Öğrenme, Veri Yoğun Uygulamalar, Biyoinformatik, YZ İlkeleri, Veri Yapıları ve Algoritmalar, NES - Nesne Yönelimli Programlama',
          languages: 'Diller',
          turkish: 'Türkçe (Ana Dil)',
          english: 'İngilizce (B2)'
        },
        experience: {
          title: 'İş Deneyimi',
          juniorDataScientist: 'Genç Veri Bilimci',
          inventAnalytics: 'Invent Analytics',
          duration: 'Mayıs 2025 – Devam Ediyor',
          location: 'Ankara, Türkiye',
          responsibilities: [
            'Python kullanarak otonom Aracı Yapay Zeka iş akışları tasarladım, operasyonel görevlerde manuel denetimi önemli ölçüde azaltan çok adımlı karar verme süreçlerinin yürütülmesini sağladım',
            'LLM\'ler ve vektör veritabanlarını kullanarak ölçeklenebilir Retrieval-Augmented Generation (RAG) hatları oluşturdum, iç bilgi erişiminin doğruluğunu ve hızını artırdım',
            'GitHub, Slack ve Jira API\'leri ile entegre akıllı aracılar geliştirip dağıttım, proje takip rutinlerini ve ekip raporlama mekanizmalarını tamamen otomatikleştirdim'
          ],
          aiResearchIntern: 'Yapay Zeka Araştırma Stajyeri',
          researchDuration: 'Ocak 2024 – Devam Ediyor',
          biologicalDataLab: 'Biyolojik Veri Bilimi Laboratuvarı, Hacettepe Üniversitesi',
          researchResponsibilities: [
            'Özel betikler yazarak uçtan uca veri işleme hatlarını otomatikleştirdim, büyük ölçekli biyolojik veri setleri için görüntü üretimi ve API veri alımını kolaylaştırdım',
            'Sıfırdan yeni bir Çok Modlu Kimyasal Dil Modeli için kod tabanını birlikte tasarladım; SMILES, moleküler metinler ve moleküler grafikleri aynı anda işlemek için Transformers kullanarak özel bir füzyon mekanizması tasarladım',
            'PyTorch kullanarak kapsamlı kıyaslama deneyleri yürüttüm, CNN tabanlı modellerin performansını en son teknoloji mimarilere karşı doğruladım'
          ],
          baykarIntern: 'YZ Yazılım Teknolojileri Stajyeri',
          baykar: 'Baykar',
          baykarDuration: 'Ağustos 2025 – Eylül 2025',
          baykarLocation: 'İstanbul, Türkiye',
          baykarResponsibilities: [
            'Havadan görüntülerde nesne tespiti ve takibi için Bilgisayarlı Görü algoritmalarının geliştirilmesine katkıda bulundum',
            'Gerçek zamanlı görü yeteneklerini artırmak için veri etiketleme, model eğitimi ve performans değerlendirmesi gerçekleştirdim'
          ]
        },
        projects: {
          title: 'Projeler',
          projectParticipant: 'Araştırmacı ve Geliştirici',
          muchem: {
            title: 'MuChem | İlaç Keşfi için Çok Modlu Derin Öğrenme',
            description: [
              'İlaç özelliklerini yüksek hassasiyetle tahmin etmek için 1D SMILES dizilerini ve 2D moleküler grafikleri birleştiren yeni bir derin öğrenme çerçevesi tasarladım',
              'PyTorch kullanarak özel bir çift kodlayıcı mekanizması tasarladım, modelin heterojen kimyasal verilerden zengin temsiller öğrenmesini sağladım'
            ]
          },
          deepscreen2: {
            title: 'DEEPScreen2 | Yapay Zeka Tabanlı İlaç Etkileşim Tahmincisi',
            description: [
              'Döndürme-değişmez görüntü artırma stratejileri uygulayarak eski CNN tabanlı hattı geliştirdim, büyük ölçekli veri setlerinde model sağlamlığını artırdım',
              'Moleküler etkileşimleri görselleştirmek için SHAP tabanlı Açıklanabilir Yapay Zeka (XAI) modüllerini entegre ettim, "kara kutu" modeli biyologlar için yorumlanabilir hale getirdim'
            ]
          },
          mooc: {
            title: 'MOOC\'lerde Erken Risk Analizi | Sıralı Veri Modelleme',
            description: [
              'İlk kurs haftalarından sıralı davranışsal verilere dayalı olarak öğrenci ayrılma risklerini tahmin etmek için LSTM ağları kullanarak bir Erken Uyarı Sistemi (EWS) tasarladım',
              'Sağlam tahmin performansı elde etmek için XGBoost, LightGBM ve klasik makine öğrenmesi algoritmalarını kullandım ve başarısızlıkların kök nedenlerini belirlemek için XAI teknikleri uyguladım'
            ]
          },
          tec: {
            title: 'İyonosferik TEC Görselleştirici | GNSS Veri Analiz Aracı',
            description: [
              'GNSS ölçümlerinden türetilen Toplam Elektron İçeriği (TEC) verilerini analiz etmek için Python kullanarak etkileşimli bir görselleştirme aracı oluşturdum',
              'IONEX formatları için ayrıştırma algoritmaları tasarladım ve iyonosferik varyasyonları ve uzay hava etkilerini izlemek için paneller tasarladım'
            ]
          },
          webInfra: {
            title: 'Uçtan Uca Web Altyapı Dağıtımı',
            description: [
              'React (Ön Uç) ve Spring Boot (Arka Uç) kullanarak iki üretime hazır web uygulamasının uçtan uca dağıtımını düzenledim',
              'Verimli trafik yönetimi için güvenli bir Linux VPS ortamı sağladım, Docker konteynerleri ve Nginx ters proxy\'yi yapılandırdım',
              'MySQL ile veritabanı yönetimini otomatikleştirdim ve sorunsuz güncellemeler için CI hatları oluşturdum'
            ]
          }
        },
        skills: {
          title: 'Teknik Beceriler',
          programming: 'Programlama ve Temel',
          programmingList: 'Python, Java, C++, SQL, NES, Veri Yapıları, Algoritmalar, Git',
          aiMl: 'Yapay Zeka ve Veri Bilimi',
          aiMlList: 'PyTorch, Scikit-learn, Pandas, NumPy, LangChain, AgenticAI, LLMs, RAG, Bilgisayarlı Görü (CNN), XAI (SHAP), Transformers',
          webDev: 'Web Geliştirme',
          webDevList: 'React.js, HTML/CSS, JavaScript, Streamlit',
          devops: 'DevOps ve Bulut',
          devopsList: 'Docker, Docker Compose, Nginx, Linux/VPS Yönetimi, MySQL, CI/CD Hatları'
        }
      }
    }
  }
};

i18n
  .use(initReactI18next)
  .init({
    resources,
    lng: 'en', // default language
    fallbackLng: 'en',
    interpolation: {
      escapeValue: false
    }
  });

export default i18n;
